import React, { useState } from 'react';
import { History, Globe } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useAdminCompanies } from '../../hooks/useCompany';
import { formatLimit } from '../../utils/planLimits';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import CreateCompanyForm from '../../components/admin/CreateCompanyForm';
import AuditDrawer from '../../components/audit/AuditDrawer';
import type { SubscriptionPlan, SubscriptionStatus } from '../../types';

const PLAN_BADGES: Record<SubscriptionPlan, string> = {
  starter: 'bg-gray-100 text-gray-800',
  business: 'bg-blue-100 text-blue-800',
  enterprise: 'bg-purple-100 text-purple-800',
};

const STATUS_BADGES: Record<SubscriptionStatus, string> = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-red-100 text-red-800',
  trial: 'bg-blue-100 text-blue-800',
};

const PLAN_LABELS: Record<SubscriptionPlan, string> = {
  starter: 'Starter',
  business: 'Business',
  enterprise: 'Enterprise',
};

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  active: 'Activa',
  suspended: 'Suspendida',
  cancelled: 'Cancelada',
  trial: 'Prueba',
};

const Companies: React.FC = () => {
  const { canManageCompanies } = usePermissions();
  const { data: companies, isLoading, error } = useAdminCompanies();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filterPlan, setFilterPlan] = useState<SubscriptionPlan | ''>('');
  const [filterStatus, setFilterStatus] = useState<SubscriptionStatus | ''>('');
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<{ id: string; name: string } | null>(null);

  const handleOpenAudit = (companyId: string, companyName: string) => {
    setSelectedCompany({ id: companyId, name: companyName });
    setAuditDrawerOpen(true);
  };

  const handleCloseAudit = () => {
    setAuditDrawerOpen(false);
    setSelectedCompany(null);
  };

  if (!canManageCompanies) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-yellow-800 mb-2">Acceso Restringido</h2>
        <p className="text-yellow-700">
          Solo los superadministradores pueden gestionar empresas.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-red-800 mb-2">Error</h2>
        <p className="text-red-700">No se pudieron cargar las empresas.</p>
      </div>
    );
  }

  const companiesList = companies?.items || [];
  const filteredCompanies = companiesList.filter((c) => {
    if (filterPlan && c.settings.subscriptionPlan !== filterPlan) return false;
    if (filterStatus && c.settings.subscriptionStatus !== filterStatus) return false;
    return true;
  });

  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(amount);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Empresas</h1>
          <p className="mt-2 text-sm text-gray-600">
            Gestión de empresas y planes de suscripción
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Crear Empresa
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <select
          value={filterPlan}
          onChange={(e) => setFilterPlan(e.target.value as SubscriptionPlan | '')}
          className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
          aria-label="Filtrar por plan"
        >
          <option value="">Todos los planes</option>
          <option value="starter">Starter</option>
          <option value="business">Business</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as SubscriptionStatus | '')}
          className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
          aria-label="Filtrar por estado"
        >
          <option value="">Todos los estados</option>
          <option value="active">Activa</option>
          <option value="suspended">Suspendida</option>
          <option value="cancelled">Cancelada</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Empresa
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                RUT
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Plan
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuarios
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Portal
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Precio
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCompanies.map((company) => (
              <tr key={company.companyId} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{company.name}</div>
                  {company.tradeName && (
                    <div className="text-sm text-gray-500">{company.tradeName}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {company.rut || company.companyId}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${PLAN_BADGES[company.settings.subscriptionPlan]}`}>
                    {PLAN_LABELS[company.settings.subscriptionPlan]}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGES[company.settings.subscriptionStatus]}`}>
                    {STATUS_LABELS[company.settings.subscriptionStatus]}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatLimit(company.settings.maxUsers, 'máx')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {company.settings.portalEnabled ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700">
                      <Globe className="w-3.5 h-3.5" />
                      {company.settings.customSubdomain || 'Sí'}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatCurrency(company.settings.priceCLP)}/mes
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleOpenAudit(company.companyId, company.name)}
                      className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                      title="Ver historial"
                    >
                      <History className="w-5 h-5" />
                    </button>
                    <a
                      href={`/companies/${company.companyId}`}
                      className="inline-flex items-center text-primary-600 hover:text-primary-900"
                      title="Ver dashboard de la empresa"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  </div>
                </td>
              </tr>
            ))}
            {filteredCompanies.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-sm text-gray-500">
                  No se encontraron empresas
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create Company Modal */}
      {showCreateForm && (
        <CreateCompanyForm onClose={() => setShowCreateForm(false)} />
      )}

      {/* Audit Drawer */}
      {selectedCompany && (
        <AuditDrawer
          entityType="COMPANY"
          entityId={selectedCompany.id}
          entityName={selectedCompany.name}
          isOpen={auditDrawerOpen}
          onClose={handleCloseAudit}
        />
      )}
    </div>
  );
};

export default Companies;
